import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import MarketSelector from './MarketSelector';
import UrlCountBadge from './UrlCountBadge';
import { usePortalSlot } from '../../../hooks/usePortalSlot';

/**
 * Clusters actions-row content (bd deepcontext-0ihe).
 *
 * The market selector + URL-count badge used to render in their own
 * bordered bar below the (now-gone, bd deepcontext-qtuu) page title. The
 * locked mockup puts them on the SAME row as the muted subtitle instead —
 * the page-header amendment's "no title" actions-row pattern: subtitle
 * left, page actions right. That row is owned by `AppLayout`, which exposes
 * a portal target at `#app-page-actions` (right) and `#app-page-context`
 * (left); this component renders nothing itself and portals its content
 * into both.
 *
 * bd deepcontext-mfoi: the market selector moved from the right
 * (`app-page-actions`) to the left (`app-page-context`), captioned "Market"
 * — the same left-of-header placement Media Planning gives its Blueprint
 * field (`MediaPlanning.tsx`'s `contextSlot`). `UrlCountBadge` stays in
 * `app-page-actions` on the right, unmoved.
 *
 * bd deepcontext-736j: My Briefs is company-wide, not market-scoped, so its
 * locked mockup drops this shared chrome entirely — no market selector, no
 * market-scoped URL count. BriefsPage renders its own subtitle + CTA in that
 * slot's row instead.
 *
 * bd deepcontext-r4xu: the overview page also drops this portalled chrome —
 * its locked mockup puts the market selector + URL-count pill in the SAME
 * row as the blueprint search bar, which needs to flex-grow into the
 * available width. `#app-page-actions` never grows past its content width
 * (a documented constraint — see `JobsToolbar.tsx`), so `ClustersPage`
 * renders `MarketSelector`/`UrlCountBadge` inline in its own controls row
 * instead of via this portal.
 */
const ClustersTopBar: React.FC = () => {
    const location = useLocation();
    const actionsSlot = usePortalSlot('app-page-actions');
    const contextSlot = usePortalSlot('app-page-context');
    if (location.pathname.startsWith('/clusters/briefs')) return null;
    if (location.pathname.startsWith('/clusters/overview')) return null;
    if (!actionsSlot || !contextSlot) return null;

    return (
        <>
            {createPortal(<MarketSelector captioned />, contextSlot)}
            {createPortal(<UrlCountBadge />, actionsSlot)}
        </>
    );
};

export default ClustersTopBar;
